<script setup lang="ts">
import { defineAsyncComponent, onMounted, onUnmounted } from 'vue';
import { onKeyStroke, whenever } from '@vueuse/core';
import { storeToRefs } from 'pinia';

import { Landing } from '@libs/trip-planner-interfaces';
import { performanceMark } from '@libs/utils';
import { isFeatureToggleActive } from '@libs/feature-toggle';

import WebComponentsInit from '../../core/components/WebComponentsInit.vue';
import {
  useBrowserStore,
  usePremadeTripStore,
  useRouterStore,
  useStatsigEventsStore,
} from '../../core/stores';
import { useMapStore } from '../../core/stores/mapStore/mapStore';
import { useRecipeStore } from '../../core/stores/recipe/recipeStore';

import TripOverviewMap from './components/TripOverviewMap.vue';
import {
  useTripOverviewPageStore,
  useTripOverviewModalsStore,
  useTripOverviewPanelsStore,
  useTripOverviewNotificationsStore,
  useTripSummaryStore,
} from './stores';
import TripOverviewPageDesktop from './TripOverviewPageDesktop.vue';
import TripOverviewPageMobile from './TripOverviewPageMobile.vue';

const TripOverviewModals = defineAsyncComponent(
  () => import('./components/TripOverviewModals.vue')
);
const TripOverviewPanels = defineAsyncComponent(
  () => import('./components/TripOverviewPanels.vue')
);
const TripOverviewNotifications = defineAsyncComponent(
  () => import('./components/TripOverviewNotifications.vue')
);

const tripOverviewPageStore = useTripOverviewPageStore();
const mapStore = useMapStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const tripOverviewNotificationsStore = useTripOverviewNotificationsStore();
const tripSummaryStore = useTripSummaryStore();
const premadeTripStore = usePremadeTripStore();
const routerStore = useRouterStore();
const browserStore = useBrowserStore();
const statsigEventsStore = useStatsigEventsStore();
const recipeStore = useRecipeStore();

const { isInfiniteLoad } = storeToRefs(tripOverviewPageStore);

function showInitialView(): void {
  if (!routerStore.isInitialLoad) {
    return;
  }

  if (recipeStore.isSingleHotelRecipe) {
    if (routerStore.landing === Landing.room) {
      tripOverviewPanelsStore.showMetaHotelDetails();
    }
  }

  routerStore.isInitialLoad = false;
}

onMounted(() => {
  performanceMark('TripOverviewPageRenderedLegacy');

  showInitialView();
  premadeTripStore.tryToFetchPremadeTripDetails();
  tripOverviewModalsStore.openModalAutomatically();
  tripOverviewPageStore.initializeView();
});

onUnmounted(() => {
  tripOverviewModalsStore.closeTripOverviewModal();
  tripOverviewPanelsStore.closeTripOverviewPanel();
  tripOverviewNotificationsStore.clearNotifications();
});

onKeyStroke('p', (event) => {
  const correctKeyShortcutTriggered = event.ctrlKey || event.metaKey;
  if (
    tripSummaryStore.isEveryIngredientAvailable &&
    correctKeyShortcutTriggered
  ) {
    statsigEventsStore.logTripOverviewPrintModeOpened({
      source: 'shortcut',
    });
    event.preventDefault();
    routerStore.navigateToTripOverviewPrint();
  }
});

whenever(
  isInfiniteLoad,
  () => {
    if (!isFeatureToggleActive('loader')) {
      tripOverviewModalsStore.openPageReloadModal();
    }
  },
  {
    once: true,
  }
);
</script>

<template>
  <!-- NOTE: Hotel and activity details panel is mounted via child route -->
  <router-view />
  <TripOverviewModals />
  <TripOverviewPanels />
  <WebComponentsInit />

  <TripOverviewNotifications />
  <TripOverviewMap
    v-if="mapStore.shouldShowFullScreenMap"
    :should-have-border-radius="false"
    @close="mapStore.closeFullScreenDynamicTripMap()"
  />

  <TripOverviewPageMobile v-if="browserStore.isSmallerThan1200px" />
  <TripOverviewPageDesktop v-else />
</template>
